import { FC, useMemo } from 'react';

import { AddDocumentIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../components/hover-card/index';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import {
  DocumentLinksSource,
  PoolPlanEventSource,
  resolveTransactionRowDocumentLinks,
} from '../utils/resolve-event-document-links.util';

export type EventTransactionFilesCellProps = {
  date: Date;
  docusignFileLinks?: string[];
  filesLinks: string[];
  linkedSignatureFileLink?: string;
  linkedSignatureId?: string;
  onDocumentClick: (link: string) => void;
  onOpenFilesModal: (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => void;
  parentEvent?: DocumentLinksSource['event'] & PoolPlanEventSource;
  signatureStatus?: string;
  stakeholderName: string;
};

export const EventTransactionFilesCell: FC<EventTransactionFilesCellProps> = ({
  date,
  docusignFileLinks,
  filesLinks,
  linkedSignatureFileLink,
  linkedSignatureId,
  parentEvent,
  onDocumentClick,
  onOpenFilesModal,
  signatureStatus,
  stakeholderName,
}) => {
  const { allLinks: documentFileLinks, docusignLinks } = useMemo(
    () =>
      resolveTransactionRowDocumentLinks(filesLinks, {
        docusignFileLinks,
        event: parentEvent,
        linkedSignatureFileLink,
        linkedSignatureId,
        signatureStatus,
      }),
    [
      docusignFileLinks,
      filesLinks,
      linkedSignatureFileLink,
      linkedSignatureId,
      parentEvent,
      signatureStatus,
    ],
  );

  const tooltipContent =
    documentFileLinks.length === 1 ? (
      getS3FileOriginalName(documentFileLinks[0])
    ) : (
      <AppFormattedMessage id="ATTACHED_FILES" values={{ count: documentFileLinks.length }} />
    );

  const handleClick = () => {
    if (documentFileLinks.length === 0) return;

    if (documentFileLinks.length === 1) {
      onDocumentClick(documentFileLinks[0]);
      return;
    }

    onOpenFilesModal(documentFileLinks, stakeholderName, date, docusignLinks);
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          className="inline-flex w-fit cursor-pointer border-0 bg-transparent p-0"
          onClick={handleClick}
          type="button"
        >
          <AddDocumentIcon className="size-6 p-[2px]" />
        </button>
      </HoverCardTrigger>
      {/* side="top": Radix defaults to bottom, which drops the tooltip over the next table row;
          the default arrow styling already points down for a top-side card. */}
      <HoverCardContent className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2" side="top">
        <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
          {tooltipContent}
        </span>
      </HoverCardContent>
    </HoverCard>
  );
};
